/**
 * SSDoctors — Dedicated doctor management page (ss* schema)
 *
 * Creation flow (2-step):
 *   Step 1 — Party picker: search existing ss* parties or create new
 *   Step 2 — Doctor profile: license, specialization, hospital, experience
 * Profile stored in party.customdata.doctor_profile (JSON)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ssPartyAPI } from '../../services/ssApi';
import {
  Page, PageHeader, Card, Modal, TabBar, Section,
  Button, Badge, Field, Input, Select, Textarea,
  Spinner, EmptyState, ErrorBanner, SuccessBanner,
  DataTable, InfoGrid, Pagination, StatBar,
  ActiveBadge,
  COLOR, FONT, RADIUS,
} from '../../components/ss';

// ── helpers ──────────────────────────────────────────────────────────────────
const fmtErr = (d, fb) => {
  if (!d) return fb;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join('; ');
  // d.detail can itself be a FastAPI validation ARRAY — never return it raw,
  // or React crashes trying to render the objects.
  if (Array.isArray(d.detail)) return d.detail.map(e => e?.msg || JSON.stringify(e)).join('; ');
  if (typeof d.detail === 'string' && d.detail) return d.detail;
  return d.message || JSON.stringify(d);
};
const fmtDate = (dt) => dt ? new Date(dt).toLocaleDateString() : '—';
// customdata arrives as a native dict from the API (JSONB column) but may be
// a JSON string in legacy rows — accept both.
const asObj = (v) => {
  if (!v) return {};
  if (typeof v === 'string') { try { return JSON.parse(v) || {}; } catch { return {}; } }
  return v;
};
const parseProfile = (row) => asObj(row?.customdata).doctor_profile || {};

const SPECIALIZATIONS = [
  'GENERAL_PRACTICE', 'INTERNAL_MEDICINE', 'CARDIOLOGY', 'ORTHOPEDICS',
  'PEDIATRICS', 'NEUROLOGY', 'ONCOLOGY', 'SURGERY', 'FAMILY_MEDICINE',
  'GERIATRICS', 'PSYCHIATRY', 'DERMATOLOGY', 'ENDOCRINOLOGY', 'NEPHROLOGY',
];

// ── "Benevita Polar" wizard kit (creation modal only) ────────────────────────
const BP = {
  primary: '#7B1E3C', primaryLight: '#94335A', sky: '#C39A4E', teal: '#00897B',
  navy: '#2B1B22', danger: '#D32F2F', warning: '#F57C00', muted: '#8b7681',
  border: '#ECE3E7', soft: '#f7f3f4',
  font: "'Inter', -apple-system, 'Segoe UI', sans-serif",
  grad: 'linear-gradient(135deg, #2B1B22, #7B1E3C)',
  gradBtn: 'linear-gradient(135deg, #7B1E3C, #94335A)',
};

const BPStyle = () => (
  <style>{`
    @keyframes bpStepIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
    .bp-step { animation: bpStepIn .2s ease both; }
    @media (prefers-reduced-motion: reduce) { .bp-step { animation: none; } }
  `}</style>
);

const bpInputBase = (focus) => ({
  width: '100%', boxSizing: 'border-box', padding: '9px 12px', fontSize: 13,
  fontFamily: BP.font, color: BP.navy, background: '#fff', borderRadius: 10,
  border: `1.5px solid ${focus ? BP.primary : '#ECE3E7'}`, outline: 'none',
  boxShadow: focus ? '0 0 0 4px rgba(123,30,60,.18)' : 'none',
  transition: 'border-color .15s, box-shadow .15s',
});

function BPInput({ suffix, style, onFocus, onBlur, ...props }) {
  const [focus, setFocus] = useState(false);
  const input = (
    <input {...props}
      onFocus={e => { setFocus(true); onFocus && onFocus(e); }}
      onBlur={e => { setFocus(false); onBlur && onBlur(e); }}
      style={{ ...bpInputBase(focus), ...(suffix ? { paddingRight: 46 } : {}), ...style }} />
  );
  if (!suffix) return input;
  return (
    <div style={{ position: 'relative' }}>
      {input}
      <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
        fontSize: 11, fontWeight: 700, color: BP.muted, pointerEvents: 'none' }}>{suffix}</span>
    </div>
  );
}

function BPSelect({ style, onFocus, onBlur, children, ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <select {...props}
      onFocus={e => { setFocus(true); onFocus && onFocus(e); }}
      onBlur={e => { setFocus(false); onBlur && onBlur(e); }}
      style={{ ...bpInputBase(focus), ...style }}>{children}</select>
  );
}

const BPField = ({ label, required, children }) => (
  <label style={{ display: 'block', fontFamily: BP.font }}>
    <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: BP.navy, marginBottom: 5 }}>
      {label}{required && <span style={{ color: BP.danger }}> *</span>}
    </span>
    {children}
  </label>
);

const BPHeader = ({ icon, title, subtitle }) => (
  <div style={{ background: BP.grad, borderRadius: 14, padding: '16px 20px', color: '#fff',
    display: 'flex', alignItems: 'center', gap: 14, fontFamily: BP.font, marginBottom: 16 }}>
    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,.14)',
      backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,.22)', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{icon}</div>
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.2px' }}>{title}</div>
      <div style={{ fontSize: 12.5, opacity: 0.85, marginTop: 2 }}>{subtitle}</div>
    </div>
  </div>
);

function BPSteps({ step, onStep, labels }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', margin: '0 0 18px', fontFamily: BP.font }}>
      {labels.map((l, i) => {
        const n = i + 1;
        const done = step > n, active = step === n;
        return (
          <React.Fragment key={l}>
            {i > 0 && <div style={{ width: 72, height: 2, margin: '13px 10px 0', background: step > i ? BP.teal : BP.border }} />}
            <div onClick={() => done && onStep(n)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, cursor: done ? 'pointer' : 'default' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 13, fontWeight: 700,
                background: active ? BP.gradBtn : done ? BP.teal : '#EEF3F9',
                color: active || done ? '#fff' : BP.muted,
                boxShadow: active ? '0 2px 8px rgba(123,30,60,.35)' : 'none' }}>
                {done ? '✓' : n}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: active ? BP.primary : done ? BP.teal : BP.muted }}>{l}</div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const BPSection = ({ icon, title, children }) => (
  <div style={{ background: '#fff', border: `1px solid ${BP.border}`, borderRadius: 14,
    padding: 18, marginBottom: 14, fontFamily: BP.font }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: BP.navy, marginBottom: 12,
      display: 'flex', alignItems: 'center', gap: 7 }}>
      <span style={{ fontSize: 14 }}>{icon}</span>{title}
    </div>
    {children}
  </div>
);

const BPChip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick} style={{
    padding: '7px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
    fontFamily: BP.font, transition: 'all .15s',
    background: active ? 'rgba(123,30,60,.10)' : '#F1F5FA',
    border: `1.5px solid ${active ? BP.primary : 'transparent'}`,
    color: active ? BP.primary : BP.muted,
  }}>{active ? '✓ ' : ''}{children}</button>
);

function BPSegment({ options, value, onChange }) {
  return (
    <div style={{ display: 'inline-flex', background: '#EEF3F9', borderRadius: 999, padding: 3, gap: 2, fontFamily: BP.font }}>
      {options.map(o => {
        const active = value === o.v;
        return (
          <button key={o.v} type="button" onClick={() => onChange(o.v)} style={{
            padding: '7px 16px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            border: 'none', transition: 'all .15s',
            background: active ? '#fff' : 'transparent',
            color: active ? BP.primary : BP.muted,
            boxShadow: active ? '0 1px 4px rgba(13,27,46,.12)' : 'none',
          }}>{o.l}</button>
        );
      })}
    </div>
  );
}

const bpInitials = (name) => (name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
const BPAvatar = ({ name, size = 36 }) => (
  <div style={{ width: size, height: size, borderRadius: '50%', background: BP.grad, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.36),
    fontWeight: 700, fontFamily: BP.font, flexShrink: 0 }}>{bpInitials(name)}</div>
);

const BPButton = ({ children, disabled, ghost, onClick, type = 'button' }) => (
  <button type={type} disabled={disabled} onClick={onClick} style={{
    padding: '10px 22px', borderRadius: 10, fontSize: 13.5, fontWeight: 700, fontFamily: BP.font,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, transition: 'all .15s',
    ...(ghost
      ? { background: 'transparent', border: `1.5px solid ${BP.border}`, color: BP.muted }
      : { background: BP.gradBtn, border: 'none', color: '#fff', boxShadow: '0 2px 10px rgba(123,30,60,.30)' }),
  }}>{children}</button>
);

const BPFooter = ({ left, right }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    marginTop: 18, paddingTop: 14, borderTop: `1px solid ${BP.border}`,
    position: 'sticky', bottom: 0, background: '#fff' }}>
    <div style={{ display: 'flex', gap: 8 }}>{left}</div>
    <div style={{ display: 'flex', gap: 8 }}>{right}</div>
  </div>
);

// ── Party picker (step 1, search mode) ───────────────────────────────────────
function PartyPicker({ onSelect, selected }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      setLoading(true);
      ssPartyAPI.list({ q, size: 15, type: 'PER' })
        .then(r => setResults(r.data || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div style={{ fontFamily: BP.font }}>
      <BPField label="Search by name">
        <BPInput
          placeholder="Type at least 2 characters…"
          value={q}
          onChange={e => setQ(e.target.value)}
          autoFocus
        />
      </BPField>

      {loading && <div style={{ textAlign: 'center', padding: 16 }}><Spinner /></div>}

      {results.length > 0 && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {results.map(r => {
            const isSel = selected?.partyid === r.partyid;
            const hasDoc = !!asObj(r.customdata).doctor_profile;
            return (
              <div key={r.partyid}
                onClick={() => !hasDoc && onSelect(isSel ? null : r)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  borderRadius: 12, border: `1.5px solid ${isSel ? BP.primary : BP.border}`,
                  background: isSel ? 'rgba(123,30,60,.06)' : '#fff',
                  cursor: hasDoc ? 'not-allowed' : 'pointer',
                  opacity: hasDoc ? 0.55 : 1, transition: 'all .15s',
                }}>
                <BPAvatar name={r.display_name || r.name} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: BP.navy }}>{r.display_name || r.name}</div>
                  <div style={{ fontSize: 11.5, color: BP.muted, marginTop: 1 }}>
                    {(r.gender === 'M' ? 'Male' : r.gender === 'F' ? 'Female' : r.gender) || 'Person'} · #{r.partyid}
                  </div>
                </div>
                {hasDoc ? (
                  <span style={{ fontSize: 11, fontWeight: 700, color: BP.warning, background: 'rgba(245,124,0,.10)',
                    border: '1px solid rgba(245,124,0,.30)', borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                    Already a doctor
                  </span>
                ) : (
                  <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${isSel ? BP.primary : '#CBD8E6'}`,
                    background: isSel ? BP.primary : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 11, fontWeight: 700, transition: 'all .15s' }}>
                    {isSel ? '✓' : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {q.length >= 2 && results.length === 0 && !loading && (
        <div style={{ color: BP.muted, fontSize: 13, padding: '12px 0' }}>
          No parties found for "{q}".
        </div>
      )}
    </div>
  );
}

// ── Register Doctor modal (2-step) ────────────────────────────────────────────
const BLANK_PROFILE = {
  license_number: '',
  license_country: 'KE',
  license_expiry: '',
  specialization: '',
  sub_specializations: [],
  hospital_affiliation: '',
  years_experience: '',
  npi_number: '',
};
const BLANK_NEW_PARTY = { firstname: '', lastname: '', gender: '', notes: '' };

function RegisterDoctorModal({ open, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [selectedParty, setSelectedParty] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newParty, setNewParty] = useState(BLANK_NEW_PARTY);
  const [profile, setProfile] = useState(BLANK_PROFILE);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setStep(1); setSelectedParty(null); setCreatingNew(false);
      setNewParty(BLANK_NEW_PARTY); setProfile(BLANK_PROFILE); setErr('');
    }
  }, [open]);

  const canProceed = selectedParty || creatingNew;
  const updP = k => e => setNewParty(f => ({ ...f, [k]: e.target.value }));
  const updPr = k => e => setProfile(f => ({ ...f, [k]: e.target.value }));
  const toggleSubSpec = (s) => setProfile(f => ({
    ...f,
    sub_specializations: f.sub_specializations.includes(s)
      ? f.sub_specializations.filter(x => x !== s)
      : [...f.sub_specializations, s],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!profile.license_number.trim()) { setErr('License number is required.'); return; }
    setSaving(true); setErr('');
    try {
      let partyId;
      if (creatingNew) {
        if (!newParty.firstname.trim()) { setErr('First name is required.'); setSaving(false); return; }
        const created = await ssPartyAPI.create({ type: 'PER', ...newParty });
        partyId = created?.data?.partyid;
        if (!partyId) throw new Error('Party creation did not return an ID.');
      } else {
        partyId = selectedParty.partyid;
      }

      await ssPartyAPI.assignPartyRole(partyId, { role_code: 'DOCTOR' }).catch(() => {});

      await ssPartyAPI.patchCustomdata(partyId, {
        doctor_profile: {
          ...profile,
          years_experience: profile.years_experience ? Number(profile.years_experience) : null,
          license_expiry: profile.license_expiry || null,
        },
      });

      onDone();
    } catch (ex) {
      setErr(fmtErr(ex.response?.data, 'Failed to register doctor'));
    } finally {
      setSaving(false);
    }
  };

  const personName = creatingNew
    ? `${newParty.firstname} ${newParty.lastname}`.trim() || 'New person'
    : (selectedParty?.display_name || selectedParty?.name || '');

  return (
    <Modal open={open} title="Register Doctor" onClose={onClose} fullscreen>
      <BPStyle />
      <div style={{ maxWidth: 720, margin: '0 auto', fontFamily: BP.font }}>
        <BPHeader icon="⚕️" title="Register a new doctor"
          subtitle="Link a person from the registry and capture their professional profile." />

        <BPSteps step={step} onStep={(n) => { setErr(''); setStep(n); }}
          labels={['Identity', 'Professional profile']} />

        <ErrorBanner>{err}</ErrorBanner>

        {step === 1 && (
          <div className="bp-step" key="step1">
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <BPSegment
                value={creatingNew ? 'create' : 'search'}
                onChange={m => { setCreatingNew(m === 'create'); if (m === 'create') setSelectedParty(null); }}
                options={[{ v: 'search', l: 'Search existing' }, { v: 'create', l: 'Create new person' }]}
              />
            </div>

            {!creatingNew && (
              <PartyPicker selected={selectedParty} onSelect={p => setSelectedParty(p)} />
            )}

            {creatingNew && (
              <div style={{ background: BP.soft, borderRadius: 12, padding: 16, border: `1px solid ${BP.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: BP.navy, marginBottom: 12 }}>👤 New person details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <BPField label="First Name" required>
                    <BPInput required value={newParty.firstname} onChange={updP('firstname')} />
                  </BPField>
                  <BPField label="Last Name">
                    <BPInput value={newParty.lastname} onChange={updP('lastname')} />
                  </BPField>
                  <BPField label="Gender">
                    <BPSelect value={newParty.gender} onChange={updP('gender')}>
                      <option value="">— Select —</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                      <option value="O">Other</option>
                    </BPSelect>
                  </BPField>
                  <BPField label="Notes">
                    <BPInput value={newParty.notes} onChange={updP('notes')} />
                  </BPField>
                </div>
              </div>
            )}

            <BPFooter
              left={<BPButton ghost onClick={onClose}>Cancel</BPButton>}
              right={<BPButton disabled={!canProceed} onClick={() => { setErr(''); setStep(2); }}>Continue →</BPButton>}
            />
          </div>
        )}

        {step === 2 && (
          <form onSubmit={submit} className="bp-step" key="step2">
            {/* Selected person summary */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
              background: BP.soft, border: `1px solid ${BP.border}`, borderRadius: 12, marginBottom: 16 }}>
              <BPAvatar name={personName} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: BP.navy }}>
                  {creatingNew ? `New: ${personName}` : personName}
                </span>
                {!creatingNew && <span style={{ fontSize: 12, color: BP.muted, marginLeft: 8 }}>Party #{selectedParty?.partyid}</span>}
              </div>
              <button type="button" onClick={() => setStep(1)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5,
                  fontWeight: 700, color: BP.primary, fontFamily: BP.font, padding: 4 }}>← change</button>
            </div>

            <BPSection icon="🪪" title="Licence">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
                <BPField label="License Number" required>
                  <BPInput required value={profile.license_number} onChange={updPr('license_number')} placeholder="e.g. KMA/12345" />
                </BPField>
                <BPField label="License Country">
                  <BPInput value={profile.license_country} onChange={updPr('license_country')} placeholder="KE" />
                </BPField>
                <BPField label="License Expiry">
                  <BPInput type="date" value={profile.license_expiry} onChange={updPr('license_expiry')} />
                </BPField>
              </div>
            </BPSection>

            <BPSection icon="✨" title="Specialisations">
              <div style={{ fontSize: 12, fontWeight: 600, color: BP.muted, marginBottom: 8 }}>Primary specialization</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {SPECIALIZATIONS.map(s => {
                  const active = profile.specialization === s;
                  return (
                    <BPChip key={s} active={active}
                      onClick={() => setProfile(f => ({ ...f, specialization: active ? '' : s }))}>
                      {s.replace(/_/g, ' ')}
                    </BPChip>
                  );
                })}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: BP.muted, margin: '14px 0 8px' }}>Sub-specializations (optional)</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {SPECIALIZATIONS.filter(s => s !== profile.specialization).map(s => (
                  <BPChip key={s} active={profile.sub_specializations.includes(s)} onClick={() => toggleSubSpec(s)}>
                    {s.replace(/_/g, ' ')}
                  </BPChip>
                ))}
              </div>
            </BPSection>

            <BPSection icon="📍" title="Practice & coverage">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                <BPField label="Hospital Affiliation">
                  <BPInput value={profile.hospital_affiliation} onChange={updPr('hospital_affiliation')} placeholder="e.g. Nairobi Hospital" />
                </BPField>
                <BPField label="Experience">
                  <BPInput type="number" min="0" max="60" suffix="yrs"
                    value={profile.years_experience} onChange={updPr('years_experience')} />
                </BPField>
                <BPField label="NPI Number">
                  <BPInput value={profile.npi_number} onChange={updPr('npi_number')} placeholder="Optional" />
                </BPField>
              </div>
            </BPSection>

            <BPFooter
              left={<>
                <BPButton ghost onClick={() => setStep(1)}>← Back</BPButton>
                <BPButton ghost onClick={onClose}>Cancel</BPButton>
              </>}
              right={<BPButton type="submit" disabled={saving}>{saving ? '⏳ Registering…' : 'Register doctor ✓'}</BPButton>}
            />
          </form>
        )}
      </div>
    </Modal>
  );
}

// ── Edit Profile modal ────────────────────────────────────────────────────────
function EditProfileModal({ open, onClose, onDone, party }) {
  const [profile, setProfile] = useState(BLANK_PROFILE);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open && party) {
      const p = parseProfile(party);
      setProfile({
        license_number:       p.license_number       || '',
        license_country:      p.license_country      || 'KE',
        license_expiry:       p.license_expiry        ? p.license_expiry.split('T')[0] : '',
        specialization:       p.specialization       || '',
        sub_specializations:  p.sub_specializations  || [],
        hospital_affiliation: p.hospital_affiliation  || '',
        years_experience:     p.years_experience     ?? '',
        npi_number:           p.npi_number           || '',
      });
      setErr('');
    }
  }, [open, party]);

  const updPr = k => e => setProfile(f => ({ ...f, [k]: e.target.value }));
  const toggleSubSpec = (s) => setProfile(f => ({
    ...f,
    sub_specializations: f.sub_specializations.includes(s)
      ? f.sub_specializations.filter(x => x !== s)
      : [...f.sub_specializations, s],
  }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await ssPartyAPI.patchCustomdata(party.partyid, {
        doctor_profile: {
          ...profile,
          years_experience: profile.years_experience ? Number(profile.years_experience) : null,
          license_expiry: profile.license_expiry || null,
        },
      });
      onDone();
    } catch (ex) { setErr(fmtErr(ex.response?.data, 'Failed to update profile')); }
    finally { setSaving(false); }
  };

  const sectionLabel = (txt) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: COLOR.textMuted, textTransform: 'uppercase',
      letterSpacing: '0.5px', marginBottom: 10, marginTop: 18, paddingBottom: 6,
      borderBottom: `1px solid ${COLOR.grayBorder}` }}>{txt}</div>
  );

  return (
    <Modal open={open} title={`Edit Doctor Profile — ${party?.display_name || party?.name || ''}`} onClose={onClose} width={700}>
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit}>
        {sectionLabel('License & Credentials')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 4 }}>
          <Field label="License Number" required style={{ marginBottom: 0 }}>
            <Input required value={profile.license_number} onChange={updPr('license_number')} />
          </Field>
          <Field label="Country" style={{ marginBottom: 0 }}>
            <Input value={profile.license_country} onChange={updPr('license_country')} />
          </Field>
          <Field label="Expiry" style={{ marginBottom: 0 }}>
            <Input type="date" value={profile.license_expiry} onChange={updPr('license_expiry')} />
          </Field>
        </div>
        {sectionLabel('Professional Details')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 4 }}>
          <Field label="Hospital Affiliation" style={{ marginBottom: 0 }}>
            <Input value={profile.hospital_affiliation} onChange={updPr('hospital_affiliation')} />
          </Field>
          <Field label="Years of Experience" style={{ marginBottom: 0 }}>
            <Input type="number" min="0" value={profile.years_experience} onChange={updPr('years_experience')} />
          </Field>
          <Field label="NPI Number" style={{ marginBottom: 0 }}>
            <Input value={profile.npi_number} onChange={updPr('npi_number')} placeholder="Optional" />
          </Field>
        </div>
        {sectionLabel('Primary Specialization')}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          {SPECIALIZATIONS.map(s => {
            const active = profile.specialization === s;
            return (
              <button key={s} type="button"
                onClick={() => setProfile(f => ({ ...f, specialization: active ? '' : s }))}
                style={{
                  padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', border: '2px solid',
                  background: active ? COLOR.blue : '#fff',
                  color: active ? '#fff' : COLOR.gray,
                  borderColor: active ? COLOR.blue : COLOR.grayBorder,
                }}>{s.replace(/_/g, ' ')}</button>
            );
          })}
        </div>
        {sectionLabel('Sub-specializations')}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {SPECIALIZATIONS.filter(s => s !== profile.specialization).map(s => {
            const active = profile.sub_specializations.includes(s);
            return (
              <button key={s} type="button" onClick={() => toggleSubSpec(s)}
                style={{
                  padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', border: '2px solid',
                  background: active ? '#7c3aed' : '#fff',
                  color: active ? '#fff' : COLOR.gray,
                  borderColor: active ? '#7c3aed' : COLOR.grayBorder,
                }}>{s.replace(/_/g, ' ')}</button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: `1px solid ${COLOR.grayBorder}`, paddingTop: 14 }}>
          <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Detail panel ──────────────────────────────────────────────────────────────
function DoctorDetail({ party, onClose, onRefresh }) {
  const [tab, setTab] = useState('profile');
  const [editOpen, setEditOpen] = useState(false);
  const p = party;
  const prof = parseProfile(p);

  const TABS = [{ id: 'profile', label: 'Profile' }, { id: 'info', label: 'Party Info' }];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLOR.grayBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLOR.text }}>{p.display_name || p.name}</div>
          <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 3 }}>Party #{p.partyid} · Doctor</div>
          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <ActiveBadge active={p.is_active} />
            {prof.specialization && <Badge color="blue">{prof.specialization.replace(/_/g, ' ')}</Badge>}
            {prof.license_number && <span style={{ fontSize: 12, color: COLOR.textMuted }}>🪪 {prof.license_number}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          <Button size="sm" onClick={() => setEditOpen(true)}>✏️ Edit Profile</Button>
          <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
        </div>
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} style={{ paddingLeft: 20, flexShrink: 0 }} />

      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {tab === 'profile' && (
          prof.license_number ? (
            <>
              <InfoGrid rows={[
                ['License #',          prof.license_number],
                ['License Country',    prof.license_country],
                ['License Expiry',     prof.license_expiry ? fmtDate(prof.license_expiry) : '—'],
                ['Hospital',           prof.hospital_affiliation || '—'],
                ['Experience',         prof.years_experience != null ? `${prof.years_experience} years` : '—'],
                ['NPI Number',         prof.npi_number || '—'],
                ['Specialization',     prof.specialization?.replace(/_/g, ' ') || '—'],
              ]} />
              {prof.sub_specializations?.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLOR.textMuted, textTransform: 'uppercase', marginBottom: 8 }}>Sub-specializations</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {prof.sub_specializations.map(s => (
                      <Badge key={s} color="purple">{s.replace(/_/g, ' ')}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <EmptyState icon="🏥" title="No doctor profile yet" message="Click 'Edit Profile' to add license and specialization details." />
          )
        )}
        {tab === 'info' && (
          <InfoGrid rows={[
            ['Party ID',   p.partyid],
            ['First Name', p.firstname],
            ['Last Name',  p.lastname],
            ['Gender',     p.gender === 'M' ? 'Male' : p.gender === 'F' ? 'Female' : p.gender],
            ['Birthdate',  p.birthdate ? p.birthdate.split('T')[0] : '—'],
            ['Notes',      p.notes],
            ['Active',     p.is_active ? 'Yes' : 'No'],
          ]} />
        )}
      </div>

      <EditProfileModal open={editOpen} party={p} onClose={() => setEditOpen(false)}
        onDone={() => { setEditOpen(false); onRefresh(); }} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const DOCTOR_COLS = [
  { key: 'display_name', label: 'Name',
    render: (v, r) => <span style={{ fontWeight: 600, fontSize: 13, color: COLOR.text }}>{v || r.name || '—'}</span> },
  { key: '_license', label: 'License #',
    render: (_, r) => { const p = parseProfile(r); return p.license_number || <span style={{ color: COLOR.textMuted, fontSize: 12 }}>—</span>; } },
  { key: '_spec', label: 'Specialization',
    render: (_, r) => { const p = parseProfile(r); return p.specialization ? <Badge color="blue">{p.specialization.replace(/_/g, ' ')}</Badge> : <span style={{ color: COLOR.textMuted, fontSize: 12 }}>—</span>; } },
  { key: '_hospital', label: 'Hospital',
    render: (_, r) => { const p = parseProfile(r); return p.hospital_affiliation || <span style={{ color: COLOR.textMuted, fontSize: 12 }}>—</span>; } },
  { key: '_exp', label: 'Experience',
    render: (_, r) => { const p = parseProfile(r); return p.years_experience != null ? `${p.years_experience} yrs` : '—'; } },
  { key: 'is_active', label: 'Status', render: v => <ActiveBadge active={v} /> },
];

export default function SSDoctors() {
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const PER = 50;
  const [q, setQ]               = useState('');
  const [selected, setSelected] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showCreate, setShowCreate]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, size: PER, role_code: 'DOCTOR' };
    if (q) params.q = q;
    ssPartyAPI.list(params)
      .then(r => { setRows(r.data || []); setTotal(r.meta?.total || 0); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page, q]);

  useEffect(() => { load(); }, [load]);

  const withProfile = rows.filter(r => parseProfile(r).license_number).length;
  const withHospital = rows.filter(r => parseProfile(r).hospital_affiliation).length;

  return (
    <div style={{ display: 'flex', gap: 20, height: '100%', fontFamily: FONT.family }}>
      <div style={{ flex: selected ? '0 0 55%' : '1 1 100%', minWidth: 0, transition: 'flex .2s', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <PageHeader
          title="Doctors"
          subtitle={`${total.toLocaleString()} registered doctors`}
          action={<Button onClick={() => setShowCreate(true)}>+ Register Doctor</Button>}
        />

        <StatBar stats={[
          { label: 'Total',          value: total,       color: COLOR.blue },
          { label: 'With Profile',   value: withProfile, color: '#10b981'  },
          { label: 'With Hospital',  value: withHospital, color: '#7c3aed' },
        ]} />

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input style={{ maxWidth: 220 }} placeholder="Search name…" value={q}
            onChange={e => { setQ(e.target.value); setPage(1); }} />
          {q && <Button variant="ghost" size="sm" onClick={() => { setQ(''); setPage(1); }}>Clear</Button>}
        </div>

        <Card style={{ padding: 0, flex: 1 }}>
          <DataTable
            columns={[
              ...DOCTOR_COLS,
              { key: '_view', label: '', render: (_, r) => (
                <Button variant="ghostBlue" size="sm" onClick={e => { e.stopPropagation(); setSelected(r.partyid); setSelectedRow(r); }}>View →</Button>
              )},
            ]}
            rows={rows}
            rowKey="partyid"
            loading={loading}
            onRowClick={r => { setSelected(r.partyid); setSelectedRow(r); }}
            selectedKey={selected}
            emptyIcon="🏥"
            emptyTitle="No doctors found"
            emptyMsg="Register your first doctor above."
          />
        </Card>
        <Pagination page={page} total={total} per={PER} onPage={setPage} />
      </div>

      {selected && selectedRow && (
        <div style={{ flex: '1 1 45%', minWidth: 0, background: '#fff', border: `1px solid ${COLOR.grayBorder}`, borderRadius: RADIUS.lg, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <DoctorDetail
            party={selectedRow}
            onClose={() => { setSelected(null); setSelectedRow(null); }}
            onRefresh={load}
          />
        </div>
      )}

      <RegisterDoctorModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onDone={() => { setShowCreate(false); load(); }}
      />
    </div>
  );
}
